<template>
  <!-- Figma node 5194:49240 / 5194:50005 — Feedback bottom sheet -->
  <div class="p-feedback-form">
    <!-- Header -->
    <div class="p-feedback-form__header">
      <p class="f-title-40-bold t-fg-primary" v-text="$t('chat.feedback.title')" />
      <p
        class="f-body-14 t-fg-primary p-feedback-form__description"
        v-text="$t('chat.feedback.description')"
      />
    </div>

    <!-- Checkbox options -->
    <div v-if="choices.length > 0" class="p-feedback-form__choices">
      <p-check-box
        v-for="choice in choices"
        :key="choice.id"
        :model-value="isSelected(choice.id)"
        :name="`choice-${choice.id}`"
        size="md"
        @update:model-value="toggle(choice.id)"
      >
        <template #append>
          <p class="f-body-14 t-fg-primary" v-text="optionLabel(choice)" />
        </template>
      </p-check-box>
    </div>

    <!-- Textarea with char counter -->
    <div class="p-feedback-form__textarea-wrapper">
      <p-textarea
        id="feedback"
        v-model="comment"
        name="feedback"
        :label="$t('chat.feedback.commentLabel')"
        :maxlength="MAX_COMMENT_LENGTH"
      />
      <p
        class="p-feedback-form__char-counter f-utility-12 t-fg-tertiary"
        v-text="`${comment.length}/${MAX_COMMENT_LENGTH}`"
      />
    </div>

    <!-- Button group -->
    <div class="p-feedback-form__actions">
      <p-button
        class="full-width"
        variant="filled"
        color="green"
        size="lg"
        :disabled="loading || (choices.length > 0 && selectedIds.length === 0)"
        @click="handleSubmit"
      >
        {{ loading ? $t('chat.feedback.submitting') : $t('chat.feedback.submit') }}
      </p-button>
      <p-button
        class="full-width p-feedback-form__later-btn"
        size="lg"
        variant="ghost"
        :disabled="loading"
        @click="handleLater"
      >
        {{ $t('chat.feedback.later') }}
      </p-button>
    </div>
  </div>
</template>

<script setup lang="ts">
// Choices come from the helpdesk feedback endpoint (`choicesByRating`) —
// real numeric IDs + server-localized text. The store passes the sorted
// list for the current rating; this component renders the localized
// label keyed by `choice.id` (chat.feedback.options.<id>) and falls
// back to the server's text when no translation is available.
const { te, t: $t } = useI18n()

interface FeedbackChoice {
  id: number
  text: string
}

const optionLabel = (choice: FeedbackChoice): string => {
  const key = `chat.feedback.options.${choice.id}`
  return te(key) ? $t(key) : choice.text
}

interface Props {
  choices?: FeedbackChoice[]
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  choices: () => [],
  loading: false,
})

const emit = defineEmits<{
  submit: [choiceIds: number[], comment: string]
  later: []
}>()

const MAX_COMMENT_LENGTH = 90

const selectedIds = ref<number[]>([])
const comment = ref('')

const isSelected = (id: number) => selectedIds.value.includes(id)

const toggle = (id: number) => {
  const idx = selectedIds.value.indexOf(id)
  if (idx > -1) selectedIds.value.splice(idx, 1)
  else selectedIds.value.push(id)
}

const handleSubmit = () => {
  // Require ≥1 reason only when choices exist; if backend returns no
  // choices for the current rating, allow comment-only submission.
  if (props.choices.length > 0 && selectedIds.value.length === 0) return
  emit('submit', [...selectedIds.value], comment.value)
}

const handleLater = () => {
  emit('later')
}
</script>

<style lang="scss" scoped>
// Figma node 5194:49240 / 5194:50005 — Feedback form (bottom sheet)
// Layout: flex-col, gap-24, px-24, pt-40, pb-iossafe(40)
// Header: title 32px bold + description 14px regular, gap-8
// Choices: flex-col, gap-0 (8px padding top/bottom per row)
// Textarea wrapper: flex-col, gap-4, relative for counter positioning
// Char counter: 12px/16px medium fg-tertiary, text-right bottom of textarea
// Buttons: flex-col, gap-8, full-width

.p-feedback-form {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__description {
    line-height: 22px;
  }

  &__choices {
    display: flex;
    flex-direction: column;
    gap: 8px;

    // Zero the browser's default <p> margin so the label sits flush with
    // the checkbox baseline. Without this the `<p>` adds top/bottom margin
    // and the checkbox appears off-center relative to its label.
    :deep(p) {
      margin: 0;
    }
  }

  &__textarea-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
  }

  &__char-counter {
    align-self: flex-end;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    text-align: right;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
